/**
 * File validation utilities for upload functionality
 */

// Configuration constants
export const FILE_VALIDATION_CONFIG = {
  // Maximum file size: 10MB
  MAX_FILE_SIZE: 10 * 1024 * 1024,
  
  // Allowed file types
  ALLOWED_TYPES: {
    'image/jpeg': { extension: '.jpg', category: 'image' },
    'image/jpg': { extension: '.jpg', category: 'image' },
    'image/png': { extension: '.png', category: 'image' },
    'application/pdf': { extension: '.pdf', category: 'document' },
    'application/msword': { extension: '.doc', category: 'document' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { extension: '.docx', category: 'document' }
  },
  
  // Timeout configurations
  UPLOAD_TIMEOUT: {
    SINGLE_FILE: 60000, // 60 seconds
    BATCH_UPLOAD: 120000 // 120 seconds
  },
  
  // Retry configuration
  MAX_RETRIES: 3,
  
  // Error messages in Arabic
  ERROR_MESSAGES: {
    INVALID_FILE: 'ملف غير صالح',
    FILE_TOO_LARGE: 'حجم الملف كبير جداً. الحد الأقصى المسموح هو 10 ميجابايت',
    INVALID_FILE_TYPE: 'نوع الملف غير مدعوم. الأنواع المدعومة: JPG, PNG, PDF, DOC, DOCX',
    EMPTY_FILE: 'الملف فارغ',
    MALICIOUS_FILENAME: 'اسم الملف يحتوي على رموز غير مسموحة',
    UPLOAD_FAILED: 'فشل في رفع الملف',
    NETWORK_ERROR: 'خطأ في الاتصال بالشبكة',
    SERVER_ERROR: 'خطأ في الخادم'
  }
};

/**
 * Validates a single file object
 * @param {File} file - The file object to validate
 * @throws {Error} - Throws error with Arabic message if validation fails
 * @returns {boolean} - Returns true if file is valid
 */
export const validateFile = (file) => {
  // Check if file exists and is a File object
  if (!file || !(file instanceof File)) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.INVALID_FILE);
  }
  
  // Check if file is empty
  if (file.size === 0) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.EMPTY_FILE);
  }
  
  // Check file size
  if (file.size > FILE_VALIDATION_CONFIG.MAX_FILE_SIZE) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.FILE_TOO_LARGE);
  }
  
  // Check file type
  if (!FILE_VALIDATION_CONFIG.ALLOWED_TYPES[file.type]) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.INVALID_FILE_TYPE);
  }
  
  // Validate filename for security
  if (!isValidFilename(file.name)) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.MALICIOUS_FILENAME);
  }
  
  return true;
};

/**
 * Validates multiple files
 * @param {File[]} files - Array of file objects to validate
 * @throws {Error} - Throws error if any file is invalid
 * @returns {boolean} - Returns true if all files are valid
 */
export const validateFiles = (files) => {
  if (!Array.isArray(files)) {
    throw new Error(FILE_VALIDATION_CONFIG.ERROR_MESSAGES.INVALID_FILE);
  }
  
  files.forEach((file, index) => {
    try {
      validateFile(file);
    } catch (error) {
      throw new Error(`الملف ${index + 1}: ${error.message}`);
    }
  });
  
  return true;
};

/**
 * Validates filename for security concerns
 * @param {string} filename - The filename to validate
 * @returns {boolean} - Returns true if filename is safe
 */
const isValidFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return false;
  }
  
  // Check for dangerous characters
  const dangerousChars = new RegExp('[<>:"/\\\\|?*]');
  if (dangerousChars.test(filename)) {
    return false;
  }
  
  // Check for control characters (char code < 32) without using control-char regex
  for (let i = 0; i < filename.length; i++) {
    if (filename.charCodeAt(i) < 32) {
      return false;
    }
  }
  
  // Check for reserved names (Windows)
  const reservedNames = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i;
  if (reservedNames.test(filename)) {
    return false;
  }
  
  // Check filename length
  if (filename.length > 255) {
    return false;
  }
  
  // Check for hidden files or files starting with dots (optional security measure)
  if (filename.startsWith('.')) {
    return false;
  }
  
  return true;
};

/**
 * Sanitizes filename by removing or replacing dangerous characters
 * @param {string} filename - The filename to sanitize
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'unnamed_file';
  }
  
  // Replace dangerous characters with underscores
  let sanitized = filename.replace(new RegExp('[<>:"/\\\\|?*]', 'g'), '_');
  
  // Replace control characters (char code < 32) with underscore without using control-char regex
  sanitized = Array.from(sanitized)
    .map((ch) => (ch.charCodeAt(0) < 32 ? '_' : ch))
    .join('');
  
  // Remove multiple consecutive underscores
  sanitized = sanitized.replace(/_+/g, '_');
  
  // Remove leading/trailing underscores and dots
  sanitized = sanitized.replace(/^[._]+|[._]+$/g, '');
  
  // Ensure filename is not empty after sanitization
  if (!sanitized) {
    sanitized = 'unnamed_file';
  }
  
  // Limit filename length
  if (sanitized.length > 200) {
    const extension = sanitized.substring(sanitized.lastIndexOf('.'));
    const name = sanitized.substring(0, 200 - extension.length);
    sanitized = name + extension;
  }
  
  return sanitized;
};

/**
 * Gets file category based on MIME type
 * @param {string} mimeType - The MIME type of the file
 * @returns {string} - File category ('image', 'document', 'unknown')
 */
export const getFileCategory = (mimeType) => {
  const typeInfo = FILE_VALIDATION_CONFIG.ALLOWED_TYPES[mimeType];
  return typeInfo ? typeInfo.category : 'unknown';
};

/**
 * Formats file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
